import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { db, functions } from "../firebase";
import { trackProductEvent } from "../lib/analytics";

const PLATFORM_OPTIONS = ["Web App", "Mobile App", "Backend API", "Web + Mobile"];
const TECHNOLOGY_OPTIONS = {
  "Web App": [
    "Next.js",
    "React",
    "Vue",
    "Angular",
    "Remix",
  ],
  "Mobile App": [
    "React Native + Firebase",
    "React Native + Node.js",
    "Flutter + Firebase",
    "Flutter + Supabase",
    "Kotlin Android",
    "SwiftUI iOS",
  ],
  "Backend API": [
    "Node.js + Express",
    "NestJS",
    "FastAPI",
    "Django",
    "Spring Boot",
  ],
  "Web + Mobile": [
    "React Native + Next.js + Firebase",
    "Flutter + Firebase",
    "Next.js + Node.js",
    "React + Node.js + Firebase",
    "Supabase Fullstack",
  ],
};

export default function ProductRoadmapBuilderPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    productName: "",
    productIdea: "",
    coreFeatures: "",
    platform: "Web App",
    technology: TECHNOLOGY_OPTIONS["Web App"][0],
    requirements: "",
  });

  const technologyOptions = useMemo(
    () => TECHNOLOGY_OPTIONS[form.platform] || [],
    [form.platform]
  );

  useEffect(() => {
    if (!technologyOptions.includes(form.technology)) {
      setForm((prev) => ({
        ...prev,
        technology: technologyOptions[0] || "",
      }));
    }
  }, [technologyOptions, form.technology]);

  const previewCards = useMemo(
    () => [
      {
        title: "Feature-first planning",
        text: "The roadmap will decide what to build first, then break each feature into practical daily work.",
      },
      {
        title: "Platform-aware flow",
        text: "Technology choices now depend on platform, so mobile products and web products get different build direction.",
      },
      {
        title: "Daily development guidance",
        text: "Each day can tell you the feature, the task, how to do it, and which references to open so you can actually build.",
      },
    ],
    []
  );

  const generateProductRoadmap = httpsCallable(functions, "generateProductRoadmap");

  const handleSubmit = async () => {
    if (!user) {
      alert("Login required");
      return;
    }

    if (
      !form.productName ||
      !form.productName ||
      !form.productIdea ||
      !form.platform ||
      !form.technology ||
      !form.coreFeatures
    ) {
      alert("Please fill all required fields");
      return;
    }

    try {
      setLoading(true);

      const response = await generateProductRoadmap({
        productName: form.productName,
        productIdea: form.productIdea,
        coreFeatures: form.coreFeatures,
        platform: form.platform,
        technology: form.technology,
        requirements: form.requirements,
      });

      const roadmap = response?.data?.roadmap;

      if (!roadmap?.phases?.length) {
        throw new Error("Invalid roadmap returned");
      }

      await setDoc(
        doc(db, "product_roadmaps", user.uid),
        {
          type: "ai-product-builder",
          product: {
            productName: form.productName,
            productIdea: form.productIdea,
            coreFeatures: form.coreFeatures,
            platform: form.platform,
            technology: form.technology,
            timelineWeeks: Number(roadmap.timelineWeeks || 0),
          },
          roadmapRef: {
            phases: roadmap.phases,
          },
          progress: {
            completedDays: {},
          },
          consistency: {
            startedAt: null,
          },
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: false }
      );

      void trackProductEvent(
        "product_roadmap_created",
        {
          platform: form.platform,
          technology: form.technology,
          timelineWeeks: Number(roadmap.timelineWeeks || 0),
        },
        { user }
      );

      navigate("/my-product-roadmap");
    } catch (error) {
      console.error("Failed to create product roadmap", error);
      const message =
        error?.message ||
        error?.details ||
        "Unable to create product roadmap right now";
      alert(`Unable to create product roadmap: ${message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page>
      <Shell
        as={motion.section}
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
      >
        <Hero>
          <Copy>
            <Eyebrow>
              <Sparkles size={14} />
              AI Product Builder
            </Eyebrow>
            <Title>Build a real product with a roadmap that feels like a self internship</Title>
            <Text>
              Start with a free product development roadmap. Turn one product idea
              into phased build work, weekly milestones, and daily development
              tasks you can follow with consistency.
            </Text>
          </Copy>
          <MiniCard>
            <span>What you are creating</span>
            <strong>Free roadmap first</strong>
            <p>Daily task board, reminders, calendar sync, and AI daily development support can sit on top as the paid consistency system.</p>
          </MiniCard>
        </Hero>

        <Grid>
          <FormCard>
            <SectionTitle>Product setup</SectionTitle>
            <FormGrid>
              <Field>
                <label>Product name</label>
                <input
                  value={form.productName}
                  onChange={(e) => setForm({ ...form, productName: e.target.value })}
                  placeholder="BuildSprint"
                />
              </Field>
              <Field>
                <label>Product idea</label>
                <textarea
                  value={form.productIdea}
                  onChange={(e) => setForm({ ...form, productIdea: e.target.value })}
                  placeholder="A teacher discovery app where students can browse teachers, open profiles, sign up, and book classes."
                  rows={4}
                />
              </Field>
              <Field>
                <label>Core product features</label>
                <textarea
                  value={form.coreFeatures}
                  onChange={(e) => setForm({ ...form, coreFeatures: e.target.value })}
                  placeholder="Splash screen, intro screens, login, signup, teacher list, teacher profile, profile setup, Firebase auth"
                  rows={4}
                />
              </Field>
              <Field>
                <label>Platform</label>
                <select
                  value={form.platform}
                  onChange={(e) => setForm({ ...form, platform: e.target.value })}
                >
                  {PLATFORM_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </Field>
              <Field>
                <label>Technology</label>
                <select
                  value={form.technology}
                  onChange={(e) => setForm({ ...form, technology: e.target.value })}
                >
                  {technologyOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </Field>
              <Field>
                <label>Important requirements or constraints</label>
                <textarea
                  value={form.requirements}
                  onChange={(e) => setForm({ ...form, requirements: e.target.value })}
                  placeholder="Mobile-first UI, Firebase for auth and database, clean onboarding, focus on MVP only."
                  rows={4}
                />
              </Field>
            </FormGrid>

            <CTA onClick={handleSubmit} disabled={loading}>
              {loading ? "Creating roadmap..." : "Create Product Roadmap"}
              <ArrowRight size={18} />
            </CTA>
          </FormCard>

          <PreviewCard>
            <SectionTitle>What the AI will generate</SectionTitle>
            <PreviewList>
              {previewCards.map((item, index) => (
                <PreviewItem key={item.title}>
                  <PreviewMeta>Layer {index + 1}</PreviewMeta>
                  <strong>{item.title}</strong>
                  <p>{item.text}</p>
                </PreviewItem>
              ))}
            </PreviewList>
          </PreviewCard>
        </Grid>
      </Shell>
    </Page>
  );
}

const Page = styled.div`
  min-height: 100vh;
  padding: 116px 0 72px;
  background:
    radial-gradient(circle at top left, rgba(245, 158, 11, 0.14), transparent 32%),
    radial-gradient(circle at top right, rgba(56, 189, 248, 0.15), transparent 28%),
    linear-gradient(180deg, #030712 0%, #081120 100%);
`;

const Shell = styled.div`
  width: min(1180px, calc(100% - 32px));
  margin: 0 auto;
`;

const Hero = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.2fr) minmax(280px, 0.8fr);
  gap: 18px;
  margin-bottom: 24px;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

const Copy = styled.div`
  padding: 28px;
  border-radius: 28px;
  background: rgba(8, 16, 29, 0.94);
  border: 1px solid rgba(148, 163, 184, 0.14);
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.2);
  color: #7dd3fc;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const Title = styled.h1`
  margin: 16px 0 12px;
  color: #f8fafc;
  font-size: clamp(2.1rem, 4vw, 3.8rem);
  line-height: 1.02;
  max-width: 14ch;
`;

const Text = styled.p`
  margin: 0;
  color: #cbd5e1;
  line-height: 1.8;
  max-width: 60ch;
`;

const MiniCard = styled.div`
  padding: 22px;
  border-radius: 28px;
  background: rgba(8, 16, 29, 0.94);
  border: 1px solid rgba(148, 163, 184, 0.14);

  span {
    display: block;
    color: #7dd3fc;
    font-size: 11px;
    font-weight: 900;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  strong {
    display: block;
    margin-top: 12px;
    color: #f8fafc;
    font-size: 1.3rem;
    line-height: 1.4;
  }

  p {
    margin: 10px 0 0;
    color: #94a3b8;
    line-height: 1.8;
    font-size: 14px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(320px, 0.92fr);
  gap: 18px;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  padding: 24px;
  border-radius: 28px;
  background: rgba(8, 16, 29, 0.94);
  border: 1px solid rgba(148, 163, 184, 0.14);
`;

const FormCard = styled(Card)``;
const PreviewCard = styled(Card)``;

const SectionTitle = styled.h2`
  margin: 0 0 18px;
  color: #f8fafc;
  font-size: 1.2rem;
`;

const FormGrid = styled.div`
  display: grid;
  gap: 14px;
`;

const Field = styled.div`
  display: grid;
  gap: 8px;

  label {
    color: #cbd5e1;
    font-size: 13px;
    font-weight: 700;
  }

  input,
  textarea,
  select {
    width: 100%;
    border-radius: 18px;
    border: 1px solid rgba(148, 163, 184, 0.14);
    background: rgba(15, 23, 42, 0.58);
    color: #f8fafc;
    padding: 14px 16px;
    font: inherit;
    resize: vertical;
  }
`;

const CTA = styled.button`
  margin-top: 18px;
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 15px 18px;
  border-radius: 18px;
  border: none;
  background: linear-gradient(135deg, #f59e0b, #38bdf8);
  color: #07111d;
  font-weight: 900;
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.7 : 1)};
`;

const PreviewList = styled.div`
  display: grid;
  gap: 12px;
`;

const PreviewItem = styled.div`
  padding: 16px;
  border-radius: 20px;
  background: rgba(15, 23, 42, 0.5);
  border: 1px solid rgba(148, 163, 184, 0.12);

  strong {
    display: block;
    color: #f8fafc;
    margin-top: 8px;
    font-size: 1rem;
  }

  p {
    margin: 8px 0 0;
    color: #94a3b8;
    font-size: 13px;
    line-height: 1.75;
  }

  small {
    display: inline-flex;
    margin-top: 12px;
    padding: 6px 10px;
    border-radius: 999px;
    background: rgba(56, 189, 248, 0.12);
    color: #7dd3fc;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }
`;

const PreviewMeta = styled.div`
  color: #fbbf24;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;
